
import { useEffect, useState, useRef } from 'react';
import { useCampaignDetails } from './hooks/useCampaignDetails';
import { usePaymentOptions } from './hooks/usePaymentOptions';
import { useTaxInvoice } from './hooks/useTaxInvoice';
import { PaymentPlanSelector } from './components/payment/PaymentPlanSelector';
import { PurchaseOrderDialog } from './components/purchase-order-v2/PurchaseOrderDialog';
import { TaxInvoiceForm } from './components/payment/TaxInvoiceForm';
import { TaxInvoicePreview } from './components/payment/TaxInvoicePreview';
import { PaymentGatewayProps } from './types/payment';
import { BANK_IBAN } from './utils/constants';
import PaymentGatewayCard from './components/payment/PaymentGatewayCard';
import BankTransferDialog from './components/payment/BankTransferDialog';
import { devLog, devError } from '@/utils/secureLogger';
import { secureStorage } from '@/utils/secureStorage';

interface EnhancedPaymentGatewayProps extends PaymentGatewayProps {
  isDraft?: boolean;
  draftId?: string | null;
}

const PaymentGateway = ({ 
  onPaymentComplete, 
  onCancel, 
  isProcessing,
  isDraft = false,
  draftId = null
}: EnhancedPaymentGatewayProps) => {
  const [amount, setAmount] = useState(0);
  const [preservedFormData, setPreservedFormData] = useState<Record<string, string | null>>({});
  const backupTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cancelInProgressRef = useRef<boolean>(false);
  const draftIdRef = useRef<string | null>(draftId);
  
  // Get the initial formData and budget calculations from secureStorage
  useEffect(() => {
    const budgetCalculations = secureStorage.get<{ totalBudget: number; totalAmount?: number }>('budgetCalculations');
    const derivedAmount = budgetCalculations?.totalBudget ?? budgetCalculations?.totalAmount;
    if (derivedAmount && typeof derivedAmount === 'number') {
      try {
        setAmount(parseFloat(String(derivedAmount)) || 0);
      } catch (err) {
        devError("Error parsing budget calculations:", err);
      }
    }
    
    // Preserve all form data on mount for potential cancellation
    const dataToPreserve = {
      creatives: localStorage.getItem('creatives'),
      currentStep: localStorage.getItem('currentStep'),
      formData: localStorage.getItem('formData'),
      selectedLocations: localStorage.getItem('selectedLocations'),
      duration: localStorage.getItem('duration'),
      devices: localStorage.getItem('devices'),
      creativeUrls: localStorage.getItem('creativeUrls'),
      locationCreatives: localStorage.getItem('locationCreatives'),
      mediaOwnerCreatives: localStorage.getItem('mediaOwnerCreatives'),
      campaignName: localStorage.getItem('campaignName'),
      useLocationSpecificCreatives: localStorage.getItem('useLocationSpecificCreatives'),
      useMediaOwnerSpecificCreatives: localStorage.getItem('useMediaOwnerSpecificCreatives'),
      allCreativeUrls: localStorage.getItem('allCreativeUrls'),
      creativesBackup: localStorage.getItem('creativesBackup'),
      // Add draft-specific data
      draftCampaignId: isDraft ? draftId : localStorage.getItem('draftCampaignId')
    };
    
    setPreservedFormData(dataToPreserve);
    devLog("PaymentGateway: Preserved form data on mount");
    
    // Store draft ID for restoration
    if (isDraft && draftId) {
      draftIdRef.current = draftId;
      localStorage.setItem('draftCampaignId', draftId);
      devLog("PaymentGateway: Working with draft:", draftId);
    }
    
    // Immediately create backups of all data
    Object.entries(dataToPreserve).forEach(([key, value]) => {
      if (value && value !== 'null' && value !== 'undefined') {
        localStorage.setItem(`${key}Backup`, value);
      }
    });
    
    // Start periodic backup timer to keep backups fresh
    backupTimerRef.current = setInterval(() => {
      const updatedData: Record<string, string | null> = {};
      
      // Backup all critical form data
      const keysToBackup = [
        'creatives', 'currentStep', 'formData', 'selectedLocations', 
        'duration', 'devices', 'creativeUrls', 'locationCreatives',
        'mediaOwnerCreatives', 'campaignName', 'useLocationSpecificCreatives',
        'useMediaOwnerSpecificCreatives', 'allCreativeUrls', 'creativesBackup',
        'draftCampaignId' // Back up draft ID too
      ];
      
      keysToBackup.forEach(key => {
        const currentValue = localStorage.getItem(key);
        if (currentValue && currentValue !== 'null' && currentValue !== 'undefined') {
          localStorage.setItem(`${key}Backup`, currentValue);
          updatedData[key] = currentValue;
        }
      });
      
      devLog("PaymentGateway: Updated data backups");
    }, 3000);
    
    return () => {
      if (backupTimerRef.current) {
        clearInterval(backupTimerRef.current);
      }
    };
  }, []);
  
  // Get campaign details
  const { campaignId, companyDetails, devices, duration } = useCampaignDetails(amount);
  
  // Ensure we have a campaign ID
  useEffect(() => {
    // Check if we already have a campaign ID in localStorage
    const storedCampaignId = localStorage.getItem('currentCampaignId');
    
    // If we have a campaign ID from the fetch, store it
    if (campaignId) {
      devLog("Storing campaign ID in localStorage");
      localStorage.setItem('currentCampaignId', campaignId);
    }
  }, [campaignId]);
  
  // Use the stored campaign ID as a fallback
  const effectiveCampaignId = campaignId || localStorage.getItem('currentCampaignId') || '';

  // Custom payment completion handler for this component
  const handlePaymentCompleteWrapper = async () => {
    devLog("Payment Gateway: starting payment completion");
    
    if (!effectiveCampaignId) {
      devError("No campaign ID available for payment completion");
    }
    
    try {
      await onPaymentComplete();
      devLog("Payment Gateway: payment completion finished");
      
      // Clear the stored campaign ID after successful payment
      localStorage.removeItem('currentCampaignId');
    } catch (error) {
      devError("Payment Gateway: Error in payment completion:", error);
      throw error; // Re-throw to be caught by the payment options hook
    }
  };

  // Enhanced cancel button click handler
  const handleCancel = () => {
    if (cancelInProgressRef.current) {
      devLog("Payment cancel already in progress, ignoring duplicate cancel");
      return;
    }
    
    // Set cancel in progress flag to prevent multiple calls
    cancelInProgressRef.current = true;
    
    devLog("PaymentGateway: Cancel button clicked, calling onCancel");
    
    // Stop the backup timer
    if (backupTimerRef.current) {
      clearInterval(backupTimerRef.current);
    }
    
    // Create a snapshot of the current step before any potential changes
    const currentStep = localStorage.getItem('currentStep') || '4';
    
    // Set payment cancellation flag before anything else
    localStorage.setItem('paymentCanceled', 'true');
    localStorage.setItem('paymentCanceledAt', Date.now().toString());
    
    // Store draft-specific data for restoration
    if (isDraft || draftIdRef.current) {
      localStorage.setItem('isDraftCancellation', 'true');
      if (draftIdRef.current) {
        localStorage.setItem('canceledDraftId', draftIdRef.current);
      }
    }
    
    // Store current step in local storage to prevent resetting
    localStorage.setItem('preservedStep', currentStep);
    localStorage.setItem('preserveCampaignId', 'true');
    
    // Create comprehensive backup data
    const backupData: Record<string, string | null> = {};
    const keysToBackup = [
      'creatives', 'currentStep', 'formData', 'selectedLocations', 
      'duration', 'devices', 'creativeUrls', 'locationCreatives',
      'mediaOwnerCreatives', 'campaignName', 'useLocationSpecificCreatives',
      'useMediaOwnerSpecificCreatives', 'allCreativeUrls', 'creativesBackup',
      'draftCampaignId' // Back up draft ID too
    ];
    
    // Collect all current data
    keysToBackup.forEach(key => {
      const value = localStorage.getItem(key);
      backupData[key] = value;
      
      // Also save backup immediately
      if (value && value !== 'null' && value !== 'undefined') {
        localStorage.setItem(`${key}Backup`, value);
      }
    });
    
    // Immediately save backed up creatives
    if (backupData.creatives && backupData.creatives !== 'null') {
      localStorage.setItem('creativesBackup', backupData.creatives);
    }
    
    // First, restore all creatives and critical data from backup
    Object.entries(backupData).forEach(([key, value]) => {
      if (value && value !== 'null' && value !== 'undefined') {
        localStorage.setItem(key, value);
      }
    });
    
    // Emit a custom event for payment cancellation with all form data
    document.dispatchEvent(new CustomEvent('payment-cancel', { 
      detail: {
        preserveData: true,
        step: currentStep,
        formData: backupData,
        isDraft: isDraft || !!draftIdRef.current,
        draftId: draftIdRef.current
      }
    }));
    
    // Add multiple timed backup restores to ensure data is consistently available
    // throughout component lifecycles
    
    // First immediate restoration
    Object.entries(backupData).forEach(([key, value]) => {
      if (value && value !== 'null' && value !== 'undefined') {
        localStorage.setItem(key, value);
      }
    });
    
    // Second delayed restoration
    setTimeout(() => {
      Object.entries(backupData).forEach(([key, value]) => {
        if (value && value !== 'null' && value !== 'undefined') {
          localStorage.setItem(key, value);
        }
      });
      
      // Ensure draft ID is preserved if applicable
      if (isDraft || draftIdRef.current) {
        localStorage.setItem('draftCampaignId', draftIdRef.current || draftId || '');
      }
      
      devLog("PaymentGateway: Second pass restoration complete");
      
      // Call the parent onCancel handler
      if (onCancel) {
        onCancel();
      }
      
      // Reset cancellation flag after a delay
      setTimeout(() => {
        cancelInProgressRef.current = false;
      }, 500);
    }, 300);
  };

  // Use the payment options hook
  const {
    paymentMethod,
    paymentType,
    showPaymentForm,
    showBankDetails,
    setShowBankDetails,
    showPaymentOptions,
    showPaymentPlanSelector,
    showPurchaseOrder,
    isConfirming,
    showTaxInvoiceForm,
    selectedPaymentMethodForInvoice,
    handlePaymentMethodChange,
    handlePayNow,
    handleBankTransfer,
    handlePaymentPlanSelected,
    handlePaymentPlanCancel,
    handlePurchaseOrderComplete,
    handlePurchaseOrderCancel,
    handleBackToPaymentPlan,
    handleBankTransferConfirm,
    handleBankTransferCancel,
    handleConfirmPayment,
    handleTaxInvoiceComplete,
    handleTaxInvoiceCancel
  } = usePaymentOptions(effectiveCampaignId, handlePaymentCompleteWrapper);

  // Get amount for payment plan selector
  const storedBudget = secureStorage.get<any>('budgetCalculations') || (localStorage.getItem('budgetCalculations') ? JSON.parse(localStorage.getItem('budgetCalculations')!) : null);
  const totalAmount = storedBudget?.totalBudget ?? storedBudget?.totalAmount ?? amount;

  // Tax Invoice hook
  const {
    showTaxInvoicePreview,
    generatedInvoice,
    isGenerating,
    isProcessing: isInvoiceProcessing,
    subtotal,
    vatAmount,
    handleTaxInvoiceFormSubmit,
    handleTaxInvoiceConfirm,
    handleTaxInvoiceBack
  } = useTaxInvoice({
    campaignId: effectiveCampaignId,
    companyDetails: {
      companyName: companyDetails?.companyName || '',
      brandName: companyDetails?.brandName || '',
      personInCharge: companyDetails?.personInCharge || '',
      email: companyDetails?.email || '',
      mobile: companyDetails?.mobile || ''
    },
    totalAmount,
    paymentMethod: selectedPaymentMethodForInvoice
  });

  // Handle invoice confirmation and proceed to payment
  const handleInvoiceConfirmAndProceed = async () => {
    const success = await handleTaxInvoiceConfirm();
    if (success) {
      handleTaxInvoiceComplete();
    }
  };

  return (
    <>
      {/* Tax Invoice Form Modal */}
      {showTaxInvoiceForm && !showTaxInvoicePreview && (
        <div className="fixed inset-0 bg-muted/20 backdrop-blur-lg flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl shadow-2xl border max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <TaxInvoiceForm
                companyName={companyDetails?.companyName || ''}
                brandName={companyDetails?.brandName || ''}
                contactPerson={companyDetails?.personInCharge || ''}
                email={companyDetails?.email || ''}
                mobile={companyDetails?.mobile || ''}
                subtotal={subtotal}
                vatAmount={vatAmount}
                totalAmount={totalAmount}
                paymentMethod={selectedPaymentMethodForInvoice}
                onSubmit={handleTaxInvoiceFormSubmit}
                onCancel={handleTaxInvoiceCancel}
                isGenerating={isGenerating}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tax Invoice Preview Modal */}
      {showTaxInvoicePreview && generatedInvoice && (
        <div className="fixed inset-0 bg-muted/20 backdrop-blur-lg flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl shadow-2xl border max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <TaxInvoicePreview
                invoice={generatedInvoice}
                onConfirm={handleInvoiceConfirmAndProceed}
                onBack={handleTaxInvoiceBack}
                isProcessing={isInvoiceProcessing}
              />
            </div>
          </div>
        </div>
      )}

      {/* Payment Plan Selector Modal */}
      {showPaymentPlanSelector && (
        <div className="fixed inset-0 bg-muted/20 backdrop-blur-lg flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl shadow-2xl border max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <PaymentPlanSelector
                totalAmount={totalAmount}
                onPlanSelected={handlePaymentPlanSelected}
                onCancel={handlePaymentPlanCancel}
                isSubmitting={isConfirming}
              />
            </div>
          </div>
        </div>
      )}

      {/* Purchase Order Dialog */}
      {showPurchaseOrder && (
        <PurchaseOrderDialog
          open={showPurchaseOrder}
          onOpenChange={() => handlePurchaseOrderCancel()}
          onComplete={handlePurchaseOrderComplete}
          onBackToPaymentPlan={handleBackToPaymentPlan}
          campaignId={effectiveCampaignId}
          companyDetails={companyDetails}
          devices={devices}
          duration={duration}
          amount={totalAmount}
        />
      )}

      <PaymentGatewayCard
        showPaymentOptions={showPaymentOptions}
        showPaymentForm={showPaymentForm}
        paymentType={paymentType}
        amount={amount}
        paymentMethod={paymentMethod}
        isConfirming={isConfirming}
        isProcessing={isProcessing}
        onPayNow={handlePayNow}
        onBankTransfer={handleBankTransfer}
        onPaymentMethodChange={handlePaymentMethodChange}
        onCancel={handleCancel}
        onConfirmPayment={handleConfirmPayment}
      />

      {/* Bank Transfer Dialog - Keep for direct bank transfer option */}
      <BankTransferDialog
        open={showBankDetails}
        onOpenChange={setShowBankDetails}
        onConfirm={handleBankTransferConfirm}
        onCancel={handleBankTransferCancel}
        amount={amount}
        bankIban={BANK_IBAN}
        campaignId={effectiveCampaignId}
        companyDetails={companyDetails}
        devices={devices}
        duration={duration}
      />
    </>
  );
};

export default PaymentGateway;
